import { add, duration } from '@eturi/date-util'
import type { Writable } from '@eturi/ts-helpers'
import { assertNotNullish, notNull, pick, selectUnaryEvery, setIfNotEqual } from '@eturi/util'
import { keysToBool, keysToInt, size, toParamsURL } from '@op/util'
import type { PayloadAction } from '@reduxjs/toolkit'
import { createSelector, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { createSliceTransformer } from 'rtk-slice-transformer'
import { resetAction } from '../actions/index.js'
import { bindCreateAsyncThunkToState } from '../bindCreateAsyncThunkToState.js'
import type { HttpExtra } from '../http.js'
import type {
	Account,
	AccountDeletionRes,
	AccountDeletionStatusRes,
	AccountKeyRes,
	AccountSSOStatus,
	ChangeCredentialsRes,
	ChangePwBody,
	DecryptPemPairFn,
	DevicePairCodeBody,
	EmailVerificationState,
	EmailVerificationStatusRes,
	EncryptPrivateKeyFn,
	GenerateKeyIdFn,
	GeneratePasswordHashFn,
	GeneratePemPairFn,
	GetPubSubTokenBody,
	InitState,
	KeyExchangeBody,
	KeyInfo,
	LoginBody,
	LoginDevice,
	LoginRes,
	LoginScheme,
	NewAccount,
	NewAccountDevice,
	OnboardingSignupChoices,
	PubSubToken,
	RegisterAccountKeyArgs,
	ResetPwRes,
	ResetPwStatus,
	SSOLoginBody,
	SSOLoginParams,
	SSOLoginScheme,
	SThunk,
	SThunkState,
	TempKeyMap,
} from '../types/index.js'
import {
	APIErrorCode,
	createAPIError,
	createReqError,
	isAPIErrorType,
	isSSOLoginScheme,
	ReqErrorCode,
} from '../types/index.js'
import { toBoolAction, toIntAction } from '../util/index.js'

export type AccountState = InitState & {
	readonly account: Maybe<Account>
	readonly accountId: Maybe<string>
	readonly deviceId: Maybe<string>
	readonly email: string
	readonly encryptedPrivate: Maybe<string>
	// NOTE: encryptedPrivateId is currently not used for anything, so I'm
	//  commenting so to make the code a bit less confusing.
	// readonly encryptedPrivateId: Maybe<string>
	readonly hasPasswordError: boolean
	// readonly isEmailVerified: Maybe<boolean>
	readonly isLegacy: boolean
	readonly keys: TempKeyMap
	readonly loginScheme: Maybe<LoginScheme>
	readonly parentSecret: Maybe<string>
	readonly parentSecretId: Maybe<string>
	/**
	 * NOTE: The password field is overloaded. For password accounts, it is just the account
	 *  password, but for SSO accounts it refers only to the Vew password. The same underlying model
	 *  is used for both, but they are differentiated in selectors, so they are not used
	 *  interchangeably by apps. In other words, a password-based account never has an `ssoPassword`,
	 *  and an SSO account never has an `accountPassword`. In places where they can be used
	 *  interchangeably (key_exchange), we access the store model directly, rather than use selectors.
	 */
	readonly password: Maybe<string>
	readonly privateKeyPem: Maybe<string>
	readonly publicKeyPem: Maybe<string>
	readonly pubSubToken: Maybe<PubSubToken>
	readonly token: Maybe<string>
	readonly tokenTS: number
	readonly userId: Maybe<string>
	readonly verifyState: Maybe<EmailVerificationState>
	readonly version: number
}

type AccountStateV3 = Omit<AccountState, 'emailVerificationState'> & {
	readonly isEmailVerified: Maybe<boolean>
}

export type WithAccountState = {
	readonly account: AccountState
}

const initialState: AccountState = {
	account: null,
	accountId: null,
	deviceId: null,
	email: '',
	encryptedPrivate: null,
	hasPasswordError: false,
	isInit: false,
	isLegacy: false,
	keys: {},
	loginScheme: null,
	parentSecret: null,
	parentSecretId: null,
	password: null,
	privateKeyPem: null,
	publicKeyPem: null,
	pubSubToken: null,
	token: null,
	tokenTS: 0,
	userId: null,
	verifyState: null,
	version: 4,
}

export const accountSlice = /*@__PURE__*/ createSlice({
	name: 'account',
	initialState,
	reducers: {
		/**
		 * Used to reset the state associated with account keys when Vew password is reset from
		 * another client. In this case, we clear all the images, as well as the keys and password.
		 */
		resetSSOVewState(s) {
			if (!isSSOLoginScheme(s.loginScheme)) return
			s.password = s.privateKeyPem = s.publicKeyPem = s.encryptedPrivate = null
			s.keys = {}
		},

		// NOTE: Currently only used for testing
		setAccount(s, { payload: account }: PayloadAction<Account>) {
			s.email = account.emails[0]
			setIfNotEqual(s, 'account', account)
		},

		setAccountEmail(s, a: PayloadAction<string>) {
			s.email = a.payload
		},

		setLegacy(s, a: PayloadAction<boolean>) {
			s.isLegacy = a.payload
		},

		setTempKeys(s, a: PayloadAction<TempKeyMap>) {
			// NOTE: We always merge the TempKeyMap, b/c we assume that updates are
			//  happening only when we need to decrypt a new one.
			// FIXME: This also never will remove temp keys. This needs to be addressed.
			s.keys = { ...s.keys, ...a.payload }
		},
	},
	extraReducers: (builder) =>
		builder
			.addCase(resetAction, () => initialState)
			.addCase(checkEmailVerification.fulfilled, (s, a) => {
				s.verifyState = a.payload.email_verification.state
			})
			.addCase(fetchAccount.fulfilled, (s, a) => {
				setIfNotEqual(s, 'account', a.payload)
				s.email = a.payload.emails[0]
				s.isInit = true
			})
			.addCase(fetchPubSubToken.fulfilled, (s, { payload: token }) => {
				if (token) setIfNotEqual(s, 'pubSubToken', token)
			})
			.addCase(doLogin.fulfilled, (s, a) => {
				s.isLegacy = a.meta.arg.isLegacy
			})
			.addMatcher(isAnyOf(login.fulfilled, ssoLogin.fulfilled), (s, action) => {
				const {
					payload: [authenticatedAccount, device],
				} = action

				const email =
					ssoLogin.fulfilled.match(action) ? authenticatedAccount.emails[0] : action.meta.arg.email

				const {
					acctkey,
					login_scheme: loginScheme,
					parent_secret: parentSecret,
					parent_secret_id: parentSecretId,
					token,
					...account
				} = authenticatedAccount

				const nextState: Writable<AccountState> = {
					...s,
					account,
					accountId: account.account_id,
					email,
					encryptedPrivate: acctkey?.enc_priv,
					isInit: true,
					loginScheme,
					parentSecret,
					parentSecretId,
					publicKeyPem: acctkey?.key,
					token,
					tokenTS: Date.now(),
					verifyState: ssoLogin.fulfilled.match(action) ? 'verified' : s.verifyState,
				}

				// NOTE: We only set these fields if we have a device returned, else we
				//  would be clearing these fields for reAuth calls.
				if (device) {
					nextState.deviceId = device.device_id
					nextState.userId = device.users?.[0]
				}

				return nextState
			})
			.addMatcher(isAnyOf(setPassword.fulfilled, setEncryptedPrivate.fulfilled), (s, a) => {
				Object.assign(s, a.payload)
			}),
})

export const { resetSSOVewState, setAccount, setAccountEmail, setLegacy, setTempKeys } =
	accountSlice.actions

// Merging rehydrated state requires handling the encryption state
export const createRehydrateAuthState =
	(decryptPrivateKey: DecryptPemPairFn) =>
	async (partial: Partial<AccountState>): Promise<AccountState> => {
		// Very old Android Parent can partially rehydrate `parentSecret` but not include
		// `parentSecretId`. If this occurs, we need to assume the account is legacy.
		const isLegacy = Boolean(partial.isLegacy ?? (partial.parentSecret && !partial.parentSecretId))
		const updateModelVersionObj: Partial<Writable<AccountState>> = {}

		// Versions 1 & 2 need upgrading
		if ((partial.version || 0) < 3) {
			updateModelVersionObj.loginScheme = isLegacy ? null : 'hash2'
			if (partial.version === 1) {
				updateModelVersionObj.encryptedPrivate = null
			}
		}

		// Previous versions used a boolean for email verification. Convert this to the full enum.
		// If `isEmailVerified` is true, we can assume 'verified', otherwise, we don't have enough
		// information to determine the state, so we set it to null.
		if ((partial.version || 0) < 4) {
			const isEmailVerified = (partial as AccountStateV3).isEmailVerified
			updateModelVersionObj.verifyState = isEmailVerified ? 'verified' : null
		}

		const mergedState = {
			...initialState,
			...partial,
			...updateModelVersionObj,
			isLegacy,
			version: 4,
		}

		const partialEncState = await handleEncryptedState(decryptPrivateKey, mergedState)

		return { ...mergedState, ...partialEncState }
	}

const isSanitizeBoolAction = /*@__PURE__*/ isAnyOf(setAccountEmail)

const sanitizeAccount = (a: Account) => ({
	...pick(a, ['block_count', 'grant_count', 'last_activity', 'retired', 'skus']),
	...keysToInt(a, ['emails', 'retired_devices', 'retired_users']),
})

export const accountSliceTransformer = /*@__PURE__*/ createSliceTransformer(
	accountSlice,
	(s) => ({
		...s,
		account: sanitizeAccount(s.account!),
		...keysToBool(s, [
			'email',
			'encryptedPrivate',
			'parentSecret',
			'parentSecretId',
			'password',
			'privateKeyPem',
			'publicKeyPem',
			'pubSubToken',
			'token',
		]),
		keys: size(s.keys),
	}),
	(a) => {
		if (setAccount.match(a)) return { ...a, payload: sanitizeAccount(a.payload) }
		if (isSanitizeBoolAction(a)) return toBoolAction(a)
		if (setTempKeys.match(a)) return toIntAction(a)

		return a
	},
)

////////// Thunks //////////////////////////////////////////////////////////////

const throwIfSSO = (state: WithAccountState) => {
	if (isSSOLoginScheme(state.account.loginScheme)) {
		throw new Error(`This operation is invalid for SSO accounts`)
	}
}

const throwIfNotSSO = (state: WithAccountState) => {
	if (!isSSOLoginScheme(state.account.loginScheme)) {
		throw new Error(`This operation is invalid for password accounts`)
	}
}

const createAsyncThunk = /*@__PURE__*/ bindCreateAsyncThunkToState<SThunkState>()

type AccountCryptoState = Pick<
	AccountState,
	'encryptedPrivate' | 'password' | 'privateKeyPem' | 'publicKeyPem'
>

/**
 * This function is used to decrypt the private key and obtain the
 * private / public PEM when the state affecting these fields changes.
 * @param decryptPemPair
 * @param state
 */
const handleEncryptedState = async (
	decryptPemPair: DecryptPemPairFn,
	state: AccountCryptoState,
) => {
	const { encryptedPrivate, password } = state
	let { privateKeyPem, publicKeyPem } = state

	if (encryptedPrivate && password && !privateKeyPem) {
		try {
			const pemPair = await decryptPemPair(encryptedPrivate, password)
			privateKeyPem = pemPair.privateKey
			publicKeyPem = pemPair.publicKey
		} catch {
			return {
				encryptedPrivate,
				hasPasswordError: true,
				password: null,
				privateKeyPem: null,
				publicKeyPem: null,
			}
		}
	}

	return {
		encryptedPrivate,
		hasPasswordError: false,
		password,
		privateKeyPem,
		publicKeyPem,
	}
}

export type WithDecryptPrivateKey = {
	readonly decryptPemPair: DecryptPemPairFn
}

export type WithGenPwHash = {
	readonly generatePasswordHash: GeneratePasswordHashFn
}

export type AccountAuthArgs = WithDecryptPrivateKey & WithGenPwHash

export type AccountGenArgs = AccountAuthArgs & {
	readonly encryptPrivateKey: EncryptPrivateKeyFn
	readonly generateKeyId: GenerateKeyIdFn
	readonly generatePemPair: GeneratePemPairFn
}

type ChangePwArgs = AccountGenArgs & {
	readonly currentPw: string
	readonly isUpgrade?: boolean
	readonly newPw: string
}

export const changePw = /*@__PURE__*/ createAsyncThunk(
	'account/changePw',
	async (
		{ currentPw, isUpgrade = false, newPw, ...genArgs }: ChangePwArgs,
		{ dispatch, getState, extra: { http } },
	) => {
		let state = getState()

		throwIfSSO(state)

		const [accountId, email, isLegacyAccount] = [
			accountId$(state),
			accountEmail$(state),
			isLegacyAccount$(state),
		]

		assertNotNullish(accountId)

		// First, try to retrieve the key pair with the existing password
		if (!isLegacyAccount) {
			await dispatch(retrieveKey({ ...genArgs, password: currentPw }))
		}

		// Get the state again after setting password and fetching encrypted private
		state = getState()

		// If after setting the current password and fetching encrypted private we
		// end up with a password error, throw an invalid password error.
		if (hasPasswordError$(state)) {
			throw createAPIError(APIErrorCode.INVALID_PASSWORD)
		}

		const [privateKey, publicKey] = [privateKeyPem$(state), publicKeyPem$(state)]
		const pemPair = privateKey && publicKey ? { privateKey, publicKey } : null

		const [acctkey, new_password, password] = await Promise.all([
			// If we have a key pair for this account, we need to generate a new acctkey
			pemPair ?
				getRegisterAccountKeyArgs(accountId, newPw, {
					...genArgs,
					generatePemPair: () => Promise.resolve(pemPair),
				})
			:	Promise.resolve(null),
			// NOTE: We aren't auto-upgrading for this release. When we auto-upgrade in a future release,
			//  we'll remove this `isLegacyAccount && isUpgrade` check and go straight to password
			//  hashing for `newPassword`.
			isLegacyAccount && !isUpgrade ?
				Promise.resolve(newPw)
			:	genArgs.generatePasswordHash(newPw, email),
			isLegacyAccount ? Promise.resolve(currentPw) : genArgs.generatePasswordHash(currentPw, email),
		])

		const data: ChangePwBody = {
			login_obj: {
				...(acctkey ? { acctkey } : {}),
				email,
				new_password,
				password,
			},
		}

		const { status } = await dispatch(http.put<ChangeCredentialsRes>('/credentials', { data }))

		if (status !== 'password_updated') {
			throw new Error(`Bad status for PUT /credentials ${status}`)
		}

		// If this is an upgrade, we update to new authentication on pw change, so we're not legacy.
		isUpgrade && dispatch(setLegacy(false))

		// Re-auth for new secret, and to verify and set the new encrypted private
		await dispatch(reAuth({ ...genArgs, password: newPw })).unwrap()

		// If were previously a legacy account and upgrading, register a new account key
		if (isLegacyAccount && isUpgrade) {
			await dispatch(registerKey(genArgs)).unwrap()
		}
	},
)

type CheckSSOBaseParams = {
	readonly type: SSOLoginScheme
}

type CheckSSOAuthCodeParams = CheckSSOBaseParams & {
	readonly auth_code: string
	readonly client_id: string
	readonly redirect_uri: string
}

type CheckSSOIdTokenParams = CheckSSOBaseParams & {
	readonly id_token: string
}

export type CheckSSOParams = CheckSSOAuthCodeParams | CheckSSOIdTokenParams

/**
 * Checks the SSO status using the params. This is shared here, because it's generic across
 * platforms, but it doesn't use `createAsyncThunk`, because it's not necessary.
 *
 * @param sso
 */
export const checkAccountSSO =
	(sso: CheckSSOParams): SThunk<Promise<AccountSSOStatus | null>> =>
	async (dispatch, _, { http }) => {
		try {
			return await dispatch(
				http.post<AccountSSOStatus>('/account_sso', { data: { sso }, isUnauthenticated: true }),
			)
		} catch (e) {
			console.error('Token check failed', e)
		}

		return null
	}

export const checkEmailVerification = /*@__PURE__*/ createAsyncThunk(
	'email-verify/status',
	async (arg: HttpExtra = {}, { dispatch, extra: { http } }) => {
		return await dispatch(http.get<EmailVerificationStatusRes>('/account_verification', arg))
	},
	{
		condition: (arg, api) => {
			// Pass through if force is defined.
			if (arg?.force) return

			const verifyState = state$(api.getState()).verifyState

			// Only perform the check if the state is 'required' or null.
			if (verifyState == null || verifyState == 'required') return

			// If the state is 'inactive' or 'verified', we don't need to check.
			return false
		},
	},
)

export const fetchAccount = /*@__PURE__*/ createAsyncThunk(
	'account/fetch',
	async (extra: HttpExtra = {}, { dispatch, extra: { http } }) => {
		const account = await dispatch(http.get<Maybe<Account>>('/account', extra))

		assertNotNullish(account, 'Account')

		return account
	},
	{
		condition: (arg, api) => {
			if (!arg?.force && isAccountInit$(api.getState())) return false
		},
	},
)

type FetchPubSubTokenArg = {
	readonly accountUserId: string
	readonly sessionDeviceId: string
}

export const fetchPubSubToken = /*@__PURE__*/ createAsyncThunk(
	'pubSub/fetch',
	async (
		{ accountUserId, sessionDeviceId }: FetchPubSubTokenArg,
		{ dispatch, extra: { http } },
	) => {
		const data: GetPubSubTokenBody = {
			device_id: sessionDeviceId,
			token_type: 'parent_pubsub_token',
			user_id: accountUserId,
		}

		return await dispatch(http.get<Maybe<PubSubToken>>(toParamsURL('/access_token', data)))
	},
)

export const forgotPw = /*@__PURE__*/ createAsyncThunk(
	'account/forgotPw',
	async (email: string, { dispatch, extra: { http } }) => {
		const { status } = await dispatch(
			http.post<ResetPwRes>('/resetpw', { data: { email }, isUnauthenticated: true }),
		)

		if (status !== 'email_sent') {
			throw new Error(`Invalid forgot pw status ${status}`)
		}
	},
)

/**
 * Generates args for changing the password / encrypted private key. If the
 * current key pair is passed, it is re-encrypted (presumably using a new
 * password, for password change). Otherwise, a new key pair is generated.
 */
const getRegisterAccountKeyArgs = async (
	accountId: string,
	password: string,
	{ encryptPrivateKey, generateKeyId, generatePemPair, generatePasswordHash }: AccountGenArgs,
): Promise<RegisterAccountKeyArgs> => {
	const [secret, { privateKey, publicKey }] = await Promise.all([
		generatePasswordHash(password, accountId),
		generatePemPair(),
	])

	const [enc_priv, key_id] = await Promise.all([
		encryptPrivateKey(privateKey, password),
		generateKeyId(publicKey),
	])

	return {
		enc_priv,
		key: publicKey,
		key_id,
		key_type: 'rsa',
		secret,
	}
}

type LoginArgs = AccountAuthArgs & {
	readonly device?: LoginDevice
	readonly email: string
	readonly password: string
}

export const login = /*@__PURE__*/ createAsyncThunk(
	'account/login',
	async (args: LoginArgs, { dispatch, getState }) => {
		const isLegacyArgs = (isLegacy: boolean) => ({ ...args, isLegacy })

		// If we already know the account is legacy, don't try to do the new auth
		if (isLegacyAccount$(getState())) {
			return await dispatch(doLogin(isLegacyArgs(true))).unwrap()
		}

		try {
			return await dispatch(doLogin(isLegacyArgs(false))).unwrap()
		} catch (e) {
			// Re-throw if this isn't an auth scheme error. We only do the try / catch
			//  here to handle legacy authentication. All other error types need to be
			//  handled where this action is invoked.
			if (!isAPIErrorType(e, APIErrorCode.UNSUPPORTED_AUTH_SCHEME)) throw e
		}

		// If we get this far, it's a legacy account, so try that.
		return await dispatch(doLogin(isLegacyArgs(true))).unwrap()
	},
)

type SSOLoginArgs = SSOLoginParams & {
	readonly device?: LoginDevice
}

export const ssoLogin = /*@__PURE__*/ createAsyncThunk(
	'account/ssoLogin',
	async ({ device, id_token, type }: SSOLoginArgs, { dispatch, extra: { http } }) => {
		const data = { device, login_obj: { sso: { id_token, type } } } satisfies SSOLoginBody
		const ret = await dispatch(http.post<LoginRes>('/login', { data, isUnauthenticated: true }))

		assertNotNullish(ret[0], 'SSO AuthenticatedAccount')

		return ret
	},
)

/** Does the actual login call, taking in `isLegacy` to determine how */
const doLogin = /*@__PURE__*/ createAsyncThunk(
	'account/doLogin',
	async (
		{
			decryptPemPair,
			device: loginDevice,
			email,
			generatePasswordHash,
			isLegacy,
			password,
		}: LoginArgs & { readonly isLegacy: boolean },
		{ dispatch, getState, extra: { http } },
	) => {
		throwIfSSO(getState())

		const data: LoginBody = { device: loginDevice, login_obj: { email, password } }

		if (!isLegacy) {
			data.login_obj.password = await generatePasswordHash(password, email)
		}

		const ret = await dispatch(http.post<LoginRes>('/login', { data, isUnauthenticated: true }))

		assertNotNullish(ret[0], 'AuthenticatedAccount')

		const encryptedPrivate = ret[0].acctkey?.enc_priv
		/**
		 * NOTE: We need to set password first, because clients may wait for `isAuthenticated` in order
		 *  to `fetchEncryptedPrivate`, and if password is not set, we'll end up trying to
		 *  `registerKey` instead of `retrieveKey`.
		 * NOTE: We also pass the `encryptedPrivate` if it exists. We have a condition on
		 *  `fetchEncryptedPrivate` that prevents it from running if there's no `force` (polling) and
		 *  we already have an encryptedPrivate. Because the `acctkey.enc_priv` is now sent on login
		 *  if it exists, it will be set by the time `fetchEncryptedPrivate` is called, but after,
		 *  `setPassword` is called (since it's the slice matcher for this that assigns it to state).
		 *  Thus, we can either pass `encryptedPrivate` to `setPassword` here, or we could avoid
		 *  setting `encryptedPrivate` on `login`, and rely on a separate fetch. Doing it this way is
		 *  more efficient (avoiding an extra call), so that's what I chose.
		 */
		await dispatch(setPassword({ decrypt: decryptPemPair, encryptedPrivate, password }))

		return ret
	},
)

type ReAuthArg = AccountAuthArgs & {
	readonly password: string
}

export const reAuth = /*@__PURE__*/ createAsyncThunk(
	'account/reAuth',
	async ({ decryptPemPair, generatePasswordHash, password }: ReAuthArg, { dispatch, getState }) => {
		throwIfSSO(getState())

		try {
			await dispatch(
				login({
					decryptPemPair,
					email: accountEmail$(getState()),
					generatePasswordHash,
					password,
				}),
			).unwrap()
		} catch {
			// Re-authentication errors have a special code. Yay.
			throw createReqError(ReqErrorCode.REAUTH_ERROR)
		}
	},
)

export const requestAccountDeletionStatus = /*@__PURE__*/ createAsyncThunk(
	'account-delete/status',
	async (_, { dispatch, extra: { http } }) => {
		try {
			const status = await dispatch(http.get<AccountDeletionStatusRes>('/account_deletion'))
			return status.deletion_requested
		} catch {
			return false
		}
	},
)

export const requestAccountDeletion = /*@__PURE__*/ createAsyncThunk(
	'account-delete/request',
	async (_, { dispatch, extra: { http } }) => {
		try {
			const reqDelete = await dispatch(
				http.post<AccountDeletionRes>('/account_deletion', {
					data: { action: 'request_deletion' },
				}),
			)

			// Return true if the account is set for deletion or if it's already
			// set to be deleted
			return (
				reqDelete.status === 'deletion_requested' ||
				reqDelete.status === 'duplicate_request_ignored'
			)
		} catch {
			return false
		}
	},
)

export const requestVerificationEmail = /*@__PURE__*/ createAsyncThunk(
	'email-verify/request',
	async (_, { dispatch, getState, extra: { http } }) => {
		const account_id = accountId$(getState())

		assertNotNullish(account_id, 'Account Id')

		await dispatch(
			http.put('/account_verification', { data: { action: 'resend_email', account_id } }),
		)
	},
)

type UpdateAccountAttributesArg =
	| { readonly name: string; readonly value: unknown }
	| { readonly attrs: Record<string, unknown> }

/**
 * PUT /account_attributes
 * Accepts either a single `name` + `value` pair or a prebuilt `attrs` object.
 */

export const updateAccountAttributes = /*@__PURE__*/ createAsyncThunk(
	'account/updateAccountAttributes',
	async (arg: UpdateAccountAttributesArg, { dispatch, extra: { http } }) => {
		const acct_attrs = 'attrs' in arg ? arg.attrs : { [arg.name]: arg.value }

		await dispatch(http.put('/account_attributes', { data: { acct_attrs } }))
	},
)

export const setOnboardingChoices = /*@__PURE__*/ createAsyncThunk(
	'account/setOnboardingChoices',
	async (choices: OnboardingSignupChoices, { dispatch }) => {
		await dispatch(
			updateAccountAttributes({
				name: 'account.onboarding_choices',
				value: choices,
			}),
		).unwrap()
	},
)

type ResetPwArg = WithGenPwHash & {
	readonly email: string
	readonly newPw: string
	readonly token: string
}

/**
 * Tries to reset the password using the new password hash scheme. If this
 * fails with an `UNSUPPORTED_AUTH_SCHEME` code, we return `false`, otherwise,
 * we return the status.
 */
const resetPwNew = /*@__PURE__*/ createAsyncThunk(
	'account/resetPwNew',
	async (
		{ email, generatePasswordHash, newPw, token }: ResetPwArg,
		{ dispatch, extra: { http } },
	) => {
		const password = await generatePasswordHash(newPw, email)
		const res = await dispatch(
			http.post<ResetPwRes>('/resetpw', {
				data: { email, password, token },
				isUnauthenticated: true,
			}),
		)

		return res.status
	},
)

/** The fallback after `resetPwNewAction` fails with `UNSUPPORTED_AUTH_SCHEME` */
const resetPwLegacy = /*@__PURE__*/ createAsyncThunk(
	'account/resetPwLegacy',
	async (
		{ email, newPw, token }: Omit<ResetPwArg, 'generatePasswordHash'>,
		{ dispatch, extra: { http } },
	) => {
		const res = await dispatch(
			http.post<ResetPwRes>('/resetpw', {
				data: { email, password: newPw, token },
				isUnauthenticated: true,
			}),
		)

		return res.status
	},
)

/**
 * FIXME: We should really change this so there's only `resetPwNew`. There's no reason not to
 *  require the hash2 login scheme.
 */
export const resetPw = /*@__PURE__*/ createAsyncThunk(
	'account/resetpw',
	async (args: ResetPwArg & AccountGenArgs, { dispatch }) => {
		let isLegacy = false
		let status: ResetPwStatus

		try {
			status = await dispatch(resetPwNew(args)).unwrap()
		} catch (e) {
			// If this is an `UNSUPPORTED_AUTH_SCHEME` code, return special `false`
			if (isAPIErrorType(e, APIErrorCode.UNSUPPORTED_AUTH_SCHEME)) {
				isLegacy = true
				status = await dispatch(resetPwLegacy(args)).unwrap()
			} else {
				// Otherwise, re-throw the error
				throw e
			}
		}

		if (status !== 'login_updated') {
			throw new Error(`Invalid resetpw status ${status}`)
		}

		dispatch(setLegacy(isLegacy))

		// Login to get the state we need to register a new encrypted key pair
		await dispatch(login({ ...args, password: args.newPw })).unwrap()

		// Register the new encrypted key pair if we're not a legacy account
		if (!isLegacy) await dispatch(registerKey(args)).unwrap()

		// Reset the auth state (clear all the stuff we just did), as we'll redirect
		// to log in. This is more of a UX decision as the user can then confirm the
		// new password by logging themselves in.
		dispatch(resetAction('reset_pw'))
	},
)

type SetPasswordArg = {
	readonly decrypt: DecryptPemPairFn
	readonly encryptedPrivate?: Maybe<string>
	readonly password: Maybe<string>
}

export const setPassword = /*@__PURE__*/ createAsyncThunk(
	'account/setPassword',
	({ decrypt, encryptedPrivate, password }: SetPasswordArg, { getState }) => {
		const cryptoState = cryptoState$(getState())
		encryptedPrivate ||= cryptoState.encryptedPrivate
		return handleEncryptedState(decrypt, { ...cryptoState, encryptedPrivate, password })
	},
)

export const signUp =
	(newAccount: NewAccount, device: NewAccountDevice): SThunk<Promise<Account>> =>
	async (dispatch, _, { http }) => {
		return await dispatch(
			http.post<Account>('/account', { data: [newAccount, device], isUnauthenticated: true }),
		)
	}

/**
 * This is defined as an action since the memoize selector won't update based
 * on time (unless we add a periodically incrementing timestamp to the reducer
 * as well as the selector).
 */
export const isAccountTokenValid =
	(expiryInMinutes = 20): SThunk<boolean> =>
	(_, getState) => {
		const state = getState()
		const hasAccountToken = !!accountToken$(state)
		const accountTokenTs = accountTokenTS$(state)

		return hasAccountToken && +add(accountTokenTs, expiryInMinutes, 'm') > Date.now()
	}
type FetchPairCodeArg = {
	readonly userId: string
}

type PairCodeRes = {
	readonly pair_code: string
	readonly pair_code_expiry: number
}

export const fetchPairCode = /*@__PURE__*/ createAsyncThunk(
	'account/fetchPairCode',
	async ({ userId }: FetchPairCodeArg, { dispatch, extra: { http } }) => {
		const data: DevicePairCodeBody = {
			token_type: 'device_pair_code',
			user_id: userId,
		}

		const res = await dispatch(http.get<Maybe<PairCodeRes>>(toParamsURL('/access_token', data)))

		assertNotNullish(res, 'Pairing code response')

		return res
	},
)

////////// Key Exchange ////////////////////////////////////////////////////////

type ChangeSSOAccountKeyArg = AccountGenArgs & {
	// The current password is required for verification
	readonly currentPassword: string
	readonly password: string
	readonly requested_by: string
}

export const changeSSOAccountKey = /*@__PURE__*/ createAsyncThunk(
	'key-exchange/change',
	async (
		{ currentPassword, password, requested_by, ...args }: ChangeSSOAccountKeyArg,
		{ dispatch, getState, extra: { http } },
	): Promise<Maybe<KeyInfo> | 'invalid'> => {
		const state = getState()
		const accountId = accountId$(state)

		throwIfNotSSO(state)
		assertNotNullish(accountId, 'Account ID')

		// Assume a password error by default.
		let hasPasswordError = true
		let [privateKeyPem, ssoPassword] = [privateKeyPem$(state), ssoPassword$(state)]

		if (!(privateKeyPem && ssoPassword)) {
			// If there is no private key or SSO password in the state, try setting it.
			hasPasswordError = await dispatch(
				setVewPassword(args.decryptPemPair, args.generatePasswordHash, currentPassword),
			)

			// If after we set the password, there's no password error, get the new pem and password from
			// our state.
			if (!hasPasswordError) {
				const newState = getState()
				;[privateKeyPem, ssoPassword] = [privateKeyPem$(newState), ssoPassword$(newState)]
			}
		} else if (ssoPassword === currentPassword) {
			// If there is a private key and SSO password, and it's the same as our confirmation
			// password, we don't have a password error.
			hasPasswordError = false
		}

		// If there's a password-related error, return this special flag
		if (hasPasswordError) return 'invalid'

		// If there's still no private key or SSO password, we can't continue.
		if (!(privateKeyPem && ssoPassword)) {
			throw new Error(`Invalid SSO Vew state: Change`)
		}

		const [enc_priv, new_secret, secret] = await Promise.all([
			args.encryptPrivateKey(privateKeyPem, password),
			args.generatePasswordHash(password, accountId),
			args.generatePasswordHash(ssoPassword, accountId),
		])

		const data: KeyExchangeBody = {
			request_args: { enc_priv, new_secret, requested_by, secret },
			request_type: 'acctkey.changepw',
		}

		const { acctkey } = await dispatch(http.post<AccountKeyRes>('/key_exchange', { data }))

		if (acctkey) {
			await dispatch(
				setEncryptedPrivate({ decrypt: args.decryptPemPair, key: acctkey.enc_priv, password }),
			)
		}

		return acctkey
	},
)

type FetchEncryptedPrivateArg = HttpExtra<any, unknown> & AccountGenArgs

let lastFetchEncTs = 0
const FETCH_ENCRYPTED_PRIVATE_INTERVAL = /*@__PURE__*/ duration(5, 's')

export const fetchEncryptedPrivate = /*@__PURE__*/ createAsyncThunk(
	'key-exchange/fetch',
	async (arg: FetchEncryptedPrivateArg, { dispatch }) => {
		lastFetchEncTs = Date.now()

		let keyInfo: Maybe<KeyInfo> = await dispatch(retrieveKey(arg)).unwrap()
		keyInfo ||= await dispatch(registerKey(arg)).unwrap()

		assertNotNullish(keyInfo, 'KeyInfo')

		await dispatch(
			setEncryptedPrivate({ decrypt: arg.decryptPemPair, key: keyInfo.enc_priv }),
		).unwrap()
	},
	{
		condition: (arg, api) => {
			// NOTE: This needs to be throttled because of a possible race condition, where we can end
			//  up trying to create keys at the same time we are retrieving, causing some calls to fail
			if (!arg?.force && Date.now() - lastFetchEncTs < FETCH_ENCRYPTED_PRIVATE_INTERVAL) {
				return false
			}

			const state = api.getState()
			// Don't try to retrieve key if we're a legacy account, or if we already have a key, or if
			// we're an SSO account without the requirements to fetch (public key + password).
			if (
				(!arg?.force && hasEncryptedPrivate$(state)) ||
				(isSSOAccount$(state) && !(publicKeyPem$(state) && ssoPassword$(state))) ||
				isLegacyAccount$(state)
			)
				return false
		},
	},
)

type RegisterKeyArgs = AccountGenArgs & {
	readonly password?: Maybe<string>
}

/** Attempts to register a key to the account */
export const registerKey = /*@__PURE__*/ createAsyncThunk(
	'key-exchange/register',
	async ({ password, ...args }: RegisterKeyArgs, { dispatch, getState, extra: { http } }) => {
		const state = getState()
		const accountId = accountId$(state)
		password ||= state.account.password

		if (!(accountId && password)) return null

		const data: KeyExchangeBody = {
			request_args: await getRegisterAccountKeyArgs(accountId, password, args),
			request_type: 'acctkey.register',
		}

		const { acctkey } = await dispatch(http.post<AccountKeyRes>('/key_exchange', { data }))

		return acctkey
	},
)

type ResetSSOAccountKeyArg = AccountGenArgs & {
	readonly requested_by: string
	readonly password: string
}

export const resetSSOAccountKey = /*@__PURE__*/ createAsyncThunk(
	'key-exchange/reset',
	async (
		{ requested_by, ...args }: ResetSSOAccountKeyArg,
		{ dispatch, getState, extra: { http } },
	) => {
		const state = getState()
		const accountId = accountId$(state)

		throwIfNotSSO(state)
		assertNotNullish(accountId, 'Account ID')

		// First "reset" the Vew password. This essentially removes it and deletes screenshots.
		const resetData: KeyExchangeBody = {
			request_args: { requested_by },
			request_type: 'acctkey.reset',
		}
		await dispatch(http.post('/key_exchange', { data: resetData }))
		// Then register a new key with the new password.
		const keyInfo = await dispatch(registerKey(args)).unwrap()

		// Finally, set the new encrypted private and password.
		if (keyInfo) {
			await dispatch(
				setEncryptedPrivate({
					decrypt: args.decryptPemPair,
					key: keyInfo?.enc_priv,
					password: args.password,
				}),
			)
		}

		return keyInfo
	},
)

type RetrieveKeyArgs = WithGenPwHash & {
	readonly password?: Maybe<string>
}
/** Attempts to retrieve a key from the account */
export const retrieveKey = /*@__PURE__*/ createAsyncThunk(
	'key-exchange/retrieve',
	async (
		{ generatePasswordHash, password }: RetrieveKeyArgs,
		{ dispatch, getState, extra: { http } },
	) => {
		const state = getState()
		const accountId = accountId$(state)
		password ||= state.account.password

		if (!(accountId && password)) return null

		const data: KeyExchangeBody = {
			request_args: { secret: await generatePasswordHash(password, accountId) },
			request_type: 'acctkey.retrieve',
		}

		const { acctkey } = await dispatch(http.post<AccountKeyRes>('/key_exchange', { data }))

		return acctkey
	},
)

type SetEncryptedPrivateArg = {
	readonly decrypt: DecryptPemPairFn
	readonly key: Maybe<string>
	readonly password?: Maybe<string>
}

/**
 * NOTE: If the encrypted private key changes, we need to remove the PEM, so everything can be
 *  recalculated. In practice, we should almost never change encryptedPrivate keys, and in most
 *  cases, this will only go from having no key to having one. But if we do end up changing it,
 *  it'll probably happen due to a password change, and decrypting with the current password will
 *  then correctly set `hasPasswordError: true`, prompting the user to enter a new password which
 *  will decrypt the key.
 */
export const setEncryptedPrivate = /*@__PURE__*/ createAsyncThunk(
	'account/setEncryptedPrivate',
	({ decrypt, key: encryptedPrivate, password }: SetEncryptedPrivateArg, { getState }) => {
		const currentCryptoState = cryptoState$(getState())
		const { encryptedPrivate: currentEncryptedPrivate, password: currentPassword } =
			currentCryptoState

		password ||= currentPassword

		const updatedCryptoState = (
			encryptedPrivate != currentEncryptedPrivate ?
				{
					encryptedPrivate,
					password,
					privateKeyPem: null,
					publicKeyPem: null,
				}
			:	{ ...currentCryptoState, password }) satisfies AccountCryptoState

		return handleEncryptedState(decrypt, updatedCryptoState)
	},
)

/**
 * Sets the password when confirmation is required for Vew to get a decrypted private key.
 * Returns `true` if there was an error setting the password.
 */
export const setVewPassword =
	(
		decrypt: DecryptPemPairFn,
		generatePasswordHash: GeneratePasswordHashFn,
		password: string,
	): SThunk<Promise<boolean>> =>
	async (dispatch, getState) => {
		const state = getState()

		/**
		 * If this is an SSO account, and we don't have an encrypted private key, we need to set the
		 * password while fetching the encrypted private. Otherwise, we can just set the password.
		 */
		if (isSSOAccount$(state) && !hasEncryptedPrivate$(state)) {
			const keyInfo = await dispatch(retrieveKey({ generatePasswordHash, password })).unwrap()

			assertNotNullish(keyInfo)

			await dispatch(
				setPassword({ decrypt, encryptedPrivate: keyInfo.enc_priv, password }),
			).unwrap()
		} else {
			await dispatch(setPassword({ decrypt, password })).unwrap()
		}

		return hasPasswordError$(getState())
	}

////////// Selectors ///////////////////////////////////////////////////////////

const state$ = <T extends WithAccountState>(s: T) => s.account
const cryptoState$ = /*@__PURE__*/ createSelector(
	state$,
	(s): AccountCryptoState =>
		pick(s, ['encryptedPrivate', 'password', 'privateKeyPem', 'publicKeyPem']),
)

export const account$ = /*@__PURE__*/ createSelector(state$, (s) => s.account)
export const accountEmail$ = /*@__PURE__*/ createSelector(state$, (s) => s.email)
// NOTE: We fall back on `s.account?.account_id` for limited clients that may
//  have had this model without `accountId`. This was the state briefly when
//  5.0.0 was release, and was updated a few days later in 5.0.2
export const accountId$ = /*@__PURE__*/ createSelector(
	state$,
	(s): Maybe<string> => s.accountId || s.account?.account_id,
)
export const accountPassword$ = /*@__PURE__*/ createSelector(state$, (s) =>
	isSSOLoginScheme(s.loginScheme) ? null : s.password,
)
export const accountToken$ = /*@__PURE__*/ createSelector(state$, (s) => s.token)
export const accountTokenTS$ = /*@__PURE__*/ createSelector(state$, (s) => s.tokenTS)
export const accountUserId$ = /*@__PURE__*/ createSelector(
	state$,
	(s): Maybe<string> => s.userId || s.account?.users[0],
)
export const encryptedPrivate$ = /*@__PURE__*/ createSelector(state$, (s) => s.encryptedPrivate)
export const hasPasswordError$ = /*@__PURE__*/ createSelector(state$, (s) => s.hasPasswordError)
export const isAccountInit$ = /*@__PURE__*/ createSelector(state$, (s) => s.isInit)
export const needsEmailVerification$ = /*@__PURE__*/ createSelector(
	state$,
	(s) => s.verifyState === 'required',
)
export const isLegacyAccount$ = /*@__PURE__*/ createSelector(state$, (s) => s.isLegacy)
export const loginDeviceId$ = /*@__PURE__*/ createSelector(state$, (s) => s.deviceId)
export const parentSecret$ = /*@__PURE__*/ createSelector(state$, (s) => s.parentSecret)
export const parentSecretId$ = /*@__PURE__*/ createSelector(state$, (s) => s.parentSecretId)
export const privateKeyPem$ = /*@__PURE__*/ createSelector(state$, (s) => s.privateKeyPem)
export const publicKeyPem$ = /*@__PURE__*/ createSelector(state$, (s) => s.publicKeyPem)
// FIXME: Remove
export const pubSubToken$ = /*@__PURE__*/ createSelector(state$, (s) => s.pubSubToken)
export const ssoLoginScheme$ = /*@__PURE__*/ createSelector(state$, (s) =>
	isSSOLoginScheme(s.loginScheme) ? s.loginScheme : null,
)
export const ssoPassword$ = /*@__PURE__*/ createSelector(state$, (s) =>
	isSSOLoginScheme(s.loginScheme) ? s.password : null,
)
export const tempKeys$ = /*@__PURE__*/ createSelector(state$, (s) => s.keys)

export const accountCreation$ = /*@__PURE__*/ createSelector(
	account$,
	(a): Maybe<number> => a?.created,
)
export const hasEncryptedPrivate$ = /*@__PURE__*/ createSelector(encryptedPrivate$, Boolean)
export const hasPrivateKey$ = /*@__PURE__*/ createSelector(privateKeyPem$, Boolean)
export const isAuthenticated$ = /*@__PURE__*/ createSelector(
	parentSecret$,
	accountId$,
	selectUnaryEvery,
)
export const isSSOAccount$ = /*@__PURE__*/ createSelector(ssoLoginScheme$, notNull)
